<template>
  <v-dialog
    :value="show_modal"
    @input="onInput($event)"
    :persistent="!closeOutside"
    :max-width="maxWidth"
    :width="autoWidth ? 'auto ' : width"
    :scrollable="scrollable"
    :content-class="contentClass"
    transition="dialog-transition"
  >
    <v-card class="modal-container" :class="'modal-'+tipo">
      <!-- Header modal -->
      <v-card-title class="modal-header" :class="{ 'has-boton-cerrar': botonCerrar }">
        <!-- Slot para personalizar: por defecto usa prop titulo -->
        <slot name="modal-header">{{ titulo }}</slot>
        <v-spacer v-if="botonCerrar"/>
        <v-btn text icon class="boton-cerrar" v-if="botonCerrar" @click.native="close('cerrar')">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Slot para personalizar antes del content -->
      <slot name="modal-pre-content"></slot>

      <!-- Content modal: default slot -->
      <v-card-text
        class="modal-content"
        :style="{ 'height': contentHeight, 'min-height': contentMinHeight }"
      >
        <slot/>
        <!-- Slot modal detalles -->
        <div class="modal-content-details">
          <slot name="modal-content-details"></slot>
        </div>
        <div class="modal-content-loading" v-if="loading">
          <v-progress-circular indeterminate color="primary"/>
        </div>
      </v-card-text>

      <!-- Botones modal (footer modal) -->
      <v-card-actions class="modal-botones" v-if="!sinFooter">
        <!-- Slot para personalizar.
        Por defecto usa botones cancelar, cancelar y/o accion (confirmar)
        (con variable para cambiar nombre y lanzando evento)-->
        <slot name="modal-botones">
          <slot name="modal-botones-prev" class="modal-botones-prev"/>
          <v-spacer/>
          <v-btn
            text
            v-if="botonCancelar"
            @click.native="close('cancelar')"
            :disabled="botonCancelarDisabled"
          >{{ botonCancelarLabel }}
          </v-btn>
          <slot name="modal-botones-accion">
            <v-btn
              text
              v-if="botonAccionSecundaria"
              @click.native="clickAccionSecundaria(true)"
              :disabled="botonAccionSecundariaDisabled"
              :href="botonAccionSecundariaHref"
            >{{ botonAccionSecundariaLabel }}
            </v-btn>
            <v-btn
              v-if="botonAccion"
              @click.native="clickAccion()"
              :disabled="botonAccionDisabled"
              :href="botonAccionHref"
              :color="botonAccionColor"
              :text="botonAccionFlat"
            >{{ botonAccionLabel }}
            </v-btn>
          </slot>
          <v-btn
            v-if="botonAceptar"
            @click.native="clickAceptar()"
            :disabled="botonAceptarDisabled"
            :href="botonAceptarHref"
          >{{ botonAceptarLabel }}
          </v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  props: {
    // Variable para controlar si se muestra o no.
    // Se puede hacer sync ya que lanza update:show cuando cambia el estado
    show: {type: Boolean, default: false},
    // Tipo de modal (para estilizar) - Valores posibles: default, info, confirmacion, accion, exito, error, warning, disabled
    tipo: {type: String, default: 'default'},
    // Titulo aplicado si no se redefine slot modal-header
    titulo: {type: String},
    // Botones: Si no se incluye el parametro, el boton no está presente.
    botonCancelar: {type: Boolean, default: false},
    botonCancelarLabel: {type: String, default: 'Cancelar'},
    botonCancelarDisabled: {type: Boolean, default: false},
    botonAceptar: {type: Boolean, default: false},
    botonAceptarLabel: {type: String, default: 'Aceptar'},
    botonAceptarDisabled: {type: Boolean, default: false},
    botonAceptarHref: {type: String, default: null},
    botonAceptarLoading: {type: Boolean, default: false}, // Si es true, se muestra loading general al aceptar (usado cuando se va a otra pantalla)
    botonAccion: {type: Boolean, default: false},
    botonAccionLabel: {type: String, default: 'Confirmar'},
    botonAccionDisabled: {type: Boolean, default: false},
    botonAccionColor: {type: String, default: null},
    botonAccionFlat: {type: Boolean, default: false},
    botonAccionHref: {type: String, default: null},
    botonAccionLoading: {type: Boolean, default: false},
    botonAccionClose: {type: Boolean, default: false},
    botonAccionSecundaria: {type: Boolean, default: false},
    botonAccionSecundariaLabel: {type: String, default: ''},
    botonAccionSecundariaDisabled: {type: Boolean, default: false},
    botonAccionSecundariaHref: {type: String, default: null},
    botonAccionSecundariaLoading: {type: Boolean, default: false},
    botonAccionSecundariaClose: {type: Boolean, default: false},
    botonCerrar: {type: Boolean, default: false},
    sinFooter: {type: Boolean, default: false},
    // Define si el modal debe cerrarse si hace clic afuera (Si es true, se hace persistent false)
    closeOutside: {type: Boolean, default: false},
    scrollable: {type: Boolean, default: false},
    // Warning: Issue con auto prop, se debe pasa width con auto y un espacio al final. Ej: "auto "
    // https://github.com/vuetifyjs/vuetify/issues/6919
    autoWidth: {type: Boolean, default: false},
    width: {default: '600px'},
    maxWidth: {type: String, default: null},
    contentHeight: {default: 'auto'},
    contentMinHeight: {default: '0'},
    loading: {type: Boolean, default: false},
    contentClass: {type: String, default: undefined},
  },
  data() {
    return {
      show_modal: this.show
    };
  },
  watch: {
    show(value) {
      // Sólo emito eventos si cambió valor
      //  (y no se está invocando este watch porque la prop cambia como consecuencia de sync con el parent)
      if (value != this.show_modal) {
        if (value) this.open();
        else this.close();
      }
    }
  },
  methods: {
    open() {
      this.show_modal = true;
      this.$emit('update:show', true); // Evento para two-way binding de show con sync
      this.$emit('open');
    },
    onInput(show) {
      if (!show) this.close();
    },
    close(boton) {
      this.show_modal = false;
      if (boton) {
        this.$emit(boton);
      }
      this.$emit('update:show', false); // Evento para two-way binding de show con sync
      this.$emit('close');
    },
    clickAceptar() {
      if (this.botonAceptarLoading) {
        Base.showLoading();
      }
      this.$emit('aceptar');
      this.close();
    },
    clickAccion() {
      if (this.botonAccionLoading) {
        Base.showLoading();
      }
      if (this.botonAccionClose) {
        // Cierra automáticamente y emite evento
        this.close('accion');
      }
      else {
        // Sólo emite evento
        this.$emit('accion');
      }
    },
    clickAccionSecundaria() {
      if (this.botonAccionSecundariaLoading) {
        Base.showLoading();
      }
      if (this.botonAccionSecundariaClose) {
        // Cierra automáticamente y emite evento
        this.close('accion_secundaria');
      }
      else {
        // Sólo emite evento
        this.$emit('accion_secundaria');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  $modal-header-height: 70px;
  $modal-botones-height: 60px;

  .v-card__title.modal-header {
    font-size: 20px;
    font-weight: 500;
    text-transform: uppercase;
    color: color(blanco);
    background-color: color(celeste);
    min-height: $modal-header-height;
    padding: 10px 30px;
    .boton-cerrar {
      color: inherit;
      position: absolute;
      right: 10px;
      margin: 0;
      .v-icon {
        font-size: 28px;
      }
    }
    &.has-boton-cerrar {
      padding-right: 50px;
    }
  }
  .v-card__text.modal-content {
    color: color(texto-oscuro);
    position: relative;
    padding: 15px 30px;
    font-size: 16px;
    >>> .mensaje {
      white-space: pre-line;
      margin-bottom: 5px;
    }
    >>> .subtitulo {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 12px;
    }
    >>> .titulo {
      font-size: 30px;
      font-weight: 500;
    }
    .modal-content-details {
      font-size: 14px;
      color: color(gris);
    }
    .modal-content-loading {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 10;
      background: color(fondo-loading);
      display: flex;
      justify-content: center;
      align-items: center;
    }
    >>> .modal-content-error {
      white-space: pre-line;
    }
  }
  .v-card__actions.modal-botones {
    background-color: color(blanco);
    border-top: 1px solid color(celeste-claro);
    padding: 0 12px;
    overflow: hidden;
    height: $modal-botones-height;
    .v-btn {
      padding: inherit;
    }
  }
  // Modal default: header fondo azul, texto blanco
  .modal-info {
    .v-card__title.modal-header {
      background-color: color(blanco);
      color: color(dark);
      border-bottom: 1px solid #ccc;
    }
  }
  .modal-exito {
    .v-card__title.modal-header {
      background-color: color(success);
      color: color(secondary);
    }
  }
  .modal-error {
    .v-card__title.modal-header {
      background-color: color(error);
    }
  }
  .modal-warning {
    .v-card__title.modal-header {
      background-color: color(warning);
      color: color(texto-oscuro);
    }
  }
  .modal-disabled {
    .v-card__title.modal-header {
      background-color: color(gris-deshabilitado)
    }
  }
  .modal-accion {
    .v-card__title.modal-header {
      background-color: color(gris-oscuro);
    }
  }
</style>
